import { ErrorState } from '@/components/ui/error-state'
import type { ResourceId } from '@/config/access-control'
import { type AccessControlInput, useAccessControl } from '@/hooks/access-control'
import { usePageTabCounts } from '@/hooks/usePageTabCounts'
import { ShieldX } from 'lucide-react'
import { type ReactNode, useEffect, useMemo } from 'react'
import { getRouteAccessDeniedMessage } from './routeAccessMessages'

export interface AccessDeniedFallbackProps {
	title?: string
	message?: string | ReactNode
	actions?: ReactNode
	containerClassName?: string
}

/**
 * Inline "restricted access" state. Mirrors AccessDeniedPage but composes inside
 * partial-access layouts (e.g. tabs where only some subviews are forbidden).
 */
export function AccessDeniedFallback({
	title = 'Access restricted',
	message = "You don't have permission to view this content. Please contact your administrator to request access.",
	actions,
	containerClassName,
}: AccessDeniedFallbackProps) {
	return (
		<ErrorState
			title={title}
			message={message}
			showSupportEmail={false}
			containerClassName={containerClassName}
			icon={ShieldX}
			iconClassName='text-destructive'
			actions={actions}
		/>
	)
}

export interface RouteAccessBoundaryProps extends AccessControlInput {
	children: ReactNode
	title?: string
	message?: string
}

export function RouteAccessBoundary({
	children,
	title,
	message,
	action,
	resource,
	...input
}: RouteAccessBoundaryProps) {
	const isActionExplicit = action !== undefined
	const viewAccess = useAccessControl({ ...input, action: isActionExplicit ? action : 'view', resource })
	const createAccess = useAccessControl({ ...input, resource, action: 'create' })
	const editAccess = useAccessControl({ ...input, resource, action: 'edit' })
	const deleteAccess = useAccessControl({ ...input, resource, action: 'delete' })
	const downloadAccess = useAccessControl({ ...input, resource, action: 'download' })

	const access = useMemo(() => {
		if (isActionExplicit) {
			return viewAccess
		}
		const actions = [viewAccess, createAccess, editAccess, deleteAccess, downloadAccess]
		const allowedOrWarning = actions.find(a => a.state === 'allowed' || a.state === 'warning')
		if (allowedOrWarning) {
			return allowedOrWarning
		}
		const loading = actions.find(a => a.state === 'loading')
		if (loading) {
			return loading
		}
		return viewAccess
	}, [isActionExplicit, viewAccess, createAccess, editAccess, deleteAccess, downloadAccess])

	const { setTabCounts } = usePageTabCounts()

	useEffect(() => {
		if (access.state === 'denied' && setTabCounts) {
			setTabCounts({})
		}
	}, [access.state, setTabCounts])

	const resolvedMessage =
		message ??
		(resource && access.state === 'denied'
			? getRouteAccessDeniedMessage(resource as ResourceId, access.reason)
			: undefined)

	if (access.state === 'allowed' || access.state === 'warning' || access.state === 'loading') {
		return <>{children}</>
	}

	if (access.state === 'denied') {
		return <AccessDeniedFallback title={title} message={resolvedMessage} containerClassName='min-h-[50vh]' />
	}

	return <>{children}</>
}
